import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { usePatchUser } from "@/pages/admin/AdminPeople";
import { ActionDialog } from "@/components/ActionDialog";
import { Button } from "@/components/ui/button";

const TONE = { pending: "text-amber-300", approved: "text-neon-lime", declined: "text-white/50", suspended: "text-neon-pink" };

export default function AdminDrivers() {
  const [status, setStatus] = useState("pending");
  const [asking, setAsking] = useState(null);
  const patch = usePatchUser();
  const { data } = useQuery({ queryKey: ["adminDrivers", status], queryFn: () => api.get("/admin/drivers", { params: { status: status || undefined } }).then((r) => r.data) });
  const ask = async (text) => {
    try {
      await api.post(`/admin/users/${asking}/request-info`, { text });
      toast.success("Request sent");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="fade-up" data-testid="admin-drivers">
      <PageHead title="Driver applications" sub="Only admins can change driver status. Only XL-qualified vehicles can be approved at launch.">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="driver-status-filter">
          <option value="pending">Pending review</option><option value="approved">Approved</option><option value="declined">Declined</option><option value="suspended">Suspended</option><option value="">All</option>
        </select>
      </PageHead>
      {!data?.length && <p className="text-sm text-muted-foreground">No drivers in this list.</p>}
      <div className="grid gap-3 lg:grid-cols-2">
        {data?.map((u) => {
          const a = u.driver_application || {};
          return (
            <div key={u.id} className="space-y-2 rounded-xl border bg-card p-4" data-testid="driver-application-card">
              <div className="flex justify-between"><p className="font-semibold">{u.name}</p><span className={`text-xs font-semibold uppercase ${TONE[u.driver_status]}`}>{u.driver_status}</span></div>
              <p className="text-xs text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ""} · applied {when(a.applied_at || u.created_at)}</p>
              <p className="text-sm">Vehicle: {u.vehicle || "—"}</p>
              {!u.is_owner && (
                <label className="flex items-center gap-2 text-sm">
                  Vehicle type
                  <select value={a.vehicle_class || ""} onChange={(e) => patch(u.id, { vehicle_class: e.target.value }, "Vehicle type saved")} data-testid={`driver-vehicle-class-${u.id}`}
                    className="h-8 rounded-md border bg-background px-2 text-xs">
                    <option value="" disabled>Not set</option><option value="xl">XL (6+ passengers)</option><option value="sedan">Sedan</option>
                  </select>
                  {a.vehicle_class !== "xl" && <span className="text-xs text-amber-300" data-testid={`driver-xl-warning-${u.id}`}>XL only at launch</span>}
                </label>
              )}
              <p className="text-sm">License: {a.license_number || "—"} {a.license_state || ""}</p>
              <p className="text-sm">Insurance: {a.insurance_provider || "—"} {a.insurance_policy ? `#${a.insurance_policy}` : ""}</p>
              {a.info_requested && <p className="text-xs text-amber-300">Info requested: {a.info_requested}</p>}
              {!u.is_owner && (
                <div className="flex flex-wrap gap-2">
                  {u.driver_status !== "approved" && <Button size="sm" disabled={a.vehicle_class !== "xl"} onClick={() => patch(u.id, { driver_status: "approved" }, "Driver approved")} data-testid={`approve-driver-${u.id}`}>Approve</Button>}
                  {u.driver_status === "pending" && <Button size="sm" variant="outline" onClick={() => patch(u.id, { driver_status: "declined" }, "Application declined")} data-testid={`decline-driver-${u.id}`}>Decline</Button>}
                  {u.driver_status === "approved" && <Button size="sm" variant="outline" onClick={() => patch(u.id, { driver_status: "suspended" }, "Driver suspended")} data-testid={`suspend-driver-${u.id}`}>Suspend</Button>}
                  <Button size="sm" variant="ghost" onClick={() => setAsking(u.id)} data-testid={`request-info-${u.id}`}>Request info</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {asking && <ActionDialog title="Request information" placeholder="What do you need from the driver?" required cta="Send" onSubmit={ask} onClose={() => setAsking(null)} />}
    </div>
  );
}
